import { Icon } from '@natera/material/lib/icon'
import { useRef, useState } from 'react'
import { getDisplayName } from '~/entities/reviewer'
import type { DocumentFlagProjection, Flag } from '~/shared/api/data-ingestion-api/client'
import { cn } from '~/shared/lib/cn'
import { Popover, PopoverContent, PopoverTrigger, Text } from '~/shared/ui'
import { getFlagAuthors, getFlagSeverityCounts, groupFlagsBySeverity } from '../lib'
import { getFlagCreator } from '../lib/flag-comments'

function isFlag(flag: Flag | DocumentFlagProjection): flag is Flag {
  return 'flag_status' in flag
}

function getFlagAuthor(flag: Flag | DocumentFlagProjection) {
  return isFlag(flag) ? (getFlagCreator(flag)?.user ?? null) : (flag.last_commented_by ?? null)
}

const FLAG_TYPE_LABELS: Record<string, string> = {
  patient_claim: 'Patient Claim',
  credit_card: 'Credit Card',
  step_classification: 'Classification',
  step_data: 'Data Extraction',
  step_carc_rarc: 'Tagging',
  step_patient: 'Matching',
  step_eob_duplication: 'Duplication',
  step_eob_payment_deposit: 'Payment Deposit',
  step_eob_processing: 'EOB Processing',
}

function getFlagTypeLabel(flagType: string): string {
  return (
    FLAG_TYPE_LABELS[flagType] ??
    flagType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  )
}

interface FlagCellProps {
  flags: (Flag | DocumentFlagProjection)[]
}

export function FlagCell({ flags }: FlagCellProps) {
  const { note, attention } = getFlagSeverityCounts(flags)
  const authors = getFlagAuthors(flags)
  const [open, setOpen] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  if (note === 0 && attention === 0) return null

  const authorNames = authors.map(getDisplayName)
  const firstAuthor = authorNames[0]
  const secondAuthor = authorNames[1]
  const remaining = authorNames.length - 2

  const handleOpen = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    setOpen(true)
  }

  const handleClose = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150)
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverTrigger
        asChild
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        onClick={(e) => e.preventDefault()}
      >
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            {note > 0 && (
              <span className="text-primary-main flex items-center gap-0.5">
                <Icon
                  className="text-[20px]"
                  children="flag"
                />
                <Text appearance="BODY2">{note}</Text>
              </span>
            )}
            {attention > 0 && (
              <span className="text-chip-status-warning-content flex items-center gap-0.5">
                <Icon
                  className="text-[20px]"
                  children="flag"
                />
                <Text appearance="BODY2">{attention}</Text>
              </span>
            )}
          </div>
          {firstAuthor && (
            <span className="text-text-secondary truncate text-xs">{firstAuthor}</span>
          )}
          {secondAuthor && (
            <span className="text-text-secondary truncate text-xs">
              {secondAuthor}
              {remaining > 0 && ` +${remaining}`}
            </span>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={8}
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="w-[235px] p-3"
      >
        <FlagPopoverContent flags={flags} />
      </PopoverContent>
    </Popover>
  )
}

interface FlagSectionProps {
  title: string
  colorClass: string
  flags: (Flag | DocumentFlagProjection)[]
}

function FlagPopoverContent({ flags }: FlagCellProps) {
  const { note, attention } = groupFlagsBySeverity(flags)

  return (
    <div className="flex flex-col gap-2">
      {note.length > 0 && (
        <FlagSection
          title="Note"
          colorClass="text-primary-main"
          flags={note}
        />
      )}
      {attention.length > 0 && (
        <FlagSection
          title="Needs attention"
          colorClass="text-chip-status-warning-content"
          flags={attention}
        />
      )}
    </div>
  )
}

function FlagSection({ title, colorClass, flags }: FlagSectionProps) {
  return (
    <div className="flex flex-col gap-1">
      <div className={cn('flex items-center gap-1', colorClass)}>
        <Icon
          className="text-[20px]"
          children="flag"
        />
        <Text appearance="SUBTITLE2">{title}</Text>
      </div>
      <ul className="m-0 flex list-none flex-col gap-0.5 p-0 pl-1">
        {flags.map((flag) => (
          <li
            key={flag.key}
            className="text-text-secondary flex items-center gap-1"
          >
            <span className="mx-1 mt-1 block size-1 shrink-0 rounded-full bg-current" />
            <Text
              appearance="CAPTION"
              truncated
            >
              {getFlagTypeLabel(flag.flag_type)}
              {(() => {
                const author = getFlagAuthor(flag)
                return author ? <> &ndash; {getDisplayName(author)}</> : null
              })()}
            </Text>
          </li>
        ))}
      </ul>
    </div>
  )
}
